// @ts-strict-ignore
import type { WorkflowState } from 'flow/store/redux/state';
import type { CanvasRect } from 'flow/store/redux/types';
import type {
  Graph,
} from '../types';

import {
  computeEdgeState, getEdgeZIndex,
} from '../edges';

import {
  COLUMN_SPACING,
  CANVAS_HORIZONTAL_PADDING,
  ROW_SPACING,
  CANVAS_VERTICAL_PADDING,
  NODE_WIDTH,
  NODE_HEIGHT,
  LEFT_CHILD_PADDING,
} from './constants';
import type {
  NodeLineage,
  BidirectionalGraphTopology,
  AdjacencyMap,
  MaxPathLengthMap,
} from './types';
import type { WorkflowNode } from '../nodes';
import { getNodeZIndex, isNodeFolderStateCollapsed, workflowNodeType } from '../nodes';
import { sortGraphBeforePlacement } from './sorting/sorting';

/**
 * builds a Map mapping a node id and the maximum number of nodes it has connected to it
 * @param incomers Map containing index of node ids and the ids of nodes connected to it
 * @returns Map with node id to depth relationship
 */
export function getMaxPathLengthMap(incomers: AdjacencyMap): MaxPathLengthMap {
  const maxPathLengthMap: MaxPathLengthMap = new Map();

  function traverseDependencies(id: string) {
    if (maxPathLengthMap.has(id)) {
      return maxPathLengthMap.get(id);
    }

    const dependenciesForNodeId = incomers.get(id);

    if (!dependenciesForNodeId?.size) {
      const result = 0;
      maxPathLengthMap.set(id, result);
      return result;
    }

    let maxPathLengthForNodeId = -Infinity;
    for (const dependencyId of Array.from(dependenciesForNodeId.values())) {
      const result = traverseDependencies(dependencyId) + 1;
      maxPathLengthForNodeId = Math.max(maxPathLengthForNodeId, result);
    }
    maxPathLengthMap.set(id, maxPathLengthForNodeId);
    return maxPathLengthForNodeId;
  }

  for (const nodeId of Array.from(incomers.keys())) {
    traverseDependencies(nodeId);
  }

  return maxPathLengthMap;
}


/**
 * Builds a topology bundle mapping all node ids to their direct incomer and outgoer node ids.
 * @param graph Graph with nodes and edges
 * @param nodeFolder Input Attribute node folder state
 * @returns a bundle with incomer and outgoer connections per node
 */
export function getBidirectionalGraphTopology(
  graph: Graph,
  nodeFolderState: WorkflowState['ui']['nodeFolders']['inputAttribute'],
): BidirectionalGraphTopology {
  // maps a nodeId to all nodeIds that connect to that nodeId
  const incomers: AdjacencyMap = new Map();
  // maps a nodeId to all nodeIds that the nodeId connect to
  const outgoers: AdjacencyMap = new Map();


  // index the nodes into a Map by their ID for a faster lookup
  const nodesIndexedById = graph.nodes.reduce((indexedNodes, node) => {
    indexedNodes.set(node.id, node);
    if (node.type === workflowNodeType.folder && !isNodeFolderStateCollapsed(nodeFolderState)) {
      // add folder children to topology if the folder is expanded
      node.data.children.forEach(child => indexedNodes.set(child.id, child));
    }
    return indexedNodes;
  }, new Map<WorkflowNode['id'], WorkflowNode>());

  /**
   * we build 2 data structures of the same type (AdjacencyMap) in a single pass;
   * one for incomer connections (connection _to_ a node) and another for outgoer
   * connections (connections _from_ a node).
   */
  graph.edges.forEach(({ source: sourceId, target: targetId }) => {
    if (nodesIndexedById.get(sourceId)?.data?.layout.asIncomer) {
      if (!incomers.has(targetId)) {
        incomers.set(targetId, new Set());
      }
      incomers.get(targetId).add(sourceId);
    }
    if (nodesIndexedById.get(targetId)?.data?.layout.asOutgoer) {
      if (!outgoers.has(sourceId)) {
        outgoers.set(sourceId, new Set());
      }
      outgoers.get(sourceId).add(targetId);
    }
  });

  return {
    incomers,
    outgoers,
  };
}


/**
 * Get the column for a node.
 * Columns are determined by the largest dependent path a node might have. if a node has
 * no dependents, it is assigned column 0.
 */
function getNodeColumn(maxPathLengthMap: MaxPathLengthMap, nodeId: WorkflowNode['id']): number {
  return maxPathLengthMap.get(nodeId) ?? 0;
}


/**
 * get how many nodes are already positioned in that column
 */
export function getNextAvailableRowInColumn(
  desiredColumn: number, columnCountIndex: number[],
) {
  return columnCountIndex[desiredColumn];
}


/**
 * convert column and row numbers to pixels using the predefined dimensions
 */
export function getNodePosition(args: {
  row: number; column: number; isChildNode?: boolean;
}): WorkflowNode['position'] {
  const {
    row, column, isChildNode,
  } = args;

  const additionalLeftPadding = isChildNode ? LEFT_CHILD_PADDING : 0;

  const x = (
    CANVAS_HORIZONTAL_PADDING
    + (column * (NODE_WIDTH + COLUMN_SPACING))
    + additionalLeftPadding
  );

  const y = CANVAS_VERTICAL_PADDING + (row * (NODE_HEIGHT + ROW_SPACING));

  return {
    x,
    y,
  };
}

/**
 * @param graph graph of nodes and edges
 * @param node source node from which to find all connected node ids
 * @returns object containing Sets of Node ids organized by their ancestry relationship to the Node
 */
export function getAllNodeIdsConnectedToNode(
  nodeId: WorkflowNode['id'], graphTopology: BidirectionalGraphTopology,
): NodeLineage {
  if (!nodeId) {
    return {
      nodeId: null,
      directIncomers: new Set(),
      directOutgoers: new Set(),
      extendedIncomers: new Set(),
      extendedOutgoers: new Set(),
      all: new Set(),
    };
  }

  const directIncomerIdsSet: Set<WorkflowNode['id']> = new Set();
  const extendedIncomerIdsSet: Set<WorkflowNode['id']> = new Set();
  const directOutgoersIdsSet: Set<WorkflowNode['id']> = new Set();
  const extendedOutgoersIdsSet: Set<WorkflowNode['id']> = new Set();
  const allConnectedNodeIdsSet: Set<WorkflowNode['id']> = new Set();


  // recurse up the node incomers and append their ids
  function appendIncomerNodeIds(_nodeId: WorkflowNode['id']) {
    const incomerNodeIdsForNode = graphTopology.incomers.get(_nodeId);
    if (!incomerNodeIdsForNode) {
      return;
    }

    for (const incomerNodeId of Array.from(incomerNodeIdsForNode.values())) {
      if (_nodeId === nodeId) {
        directIncomerIdsSet.add(incomerNodeId);
      } else {
        extendedIncomerIdsSet.add(incomerNodeId);
      }
      allConnectedNodeIdsSet.add(incomerNodeId);
      appendIncomerNodeIds(incomerNodeId);
    }
  }

  // recurse down the node outgoers and append their ids
  function appendOutgoerNodeIds(_nodeId: WorkflowNode['id']) {
    const outgoerNodeIdsForNode = graphTopology.outgoers.get(_nodeId);
    if (!outgoerNodeIdsForNode) {
      return;
    }

    for (const outgoerNodeId of Array.from(outgoerNodeIdsForNode.values())) {
      if (_nodeId === nodeId) {
        directOutgoersIdsSet.add(outgoerNodeId);
      } else {
        extendedOutgoersIdsSet.add(outgoerNodeId);
      }
      allConnectedNodeIdsSet.add(outgoerNodeId);
      appendOutgoerNodeIds(outgoerNodeId);
    }
  }

  allConnectedNodeIdsSet.add(nodeId);
  appendIncomerNodeIds(nodeId);
  appendOutgoerNodeIds(nodeId);

  return {
    nodeId,
    directIncomers: directIncomerIdsSet,
    directOutgoers: directOutgoersIdsSet,
    extendedIncomers: extendedIncomerIdsSet,
    extendedOutgoers: extendedOutgoersIdsSet,
    all: allConnectedNodeIdsSet,
  };
}


/**
 * Split out the nodes in a graph by their layout mode
 * @param graph graph of nodes and edges
 * @returns object containing all nodes split in two categories: auto and manual layout modes
 */
export function splitGraphNodesByLayoutMode(graph: Graph): {
  autoLayoutNodes: WorkflowNode[]; manualLayoutNodes: WorkflowNode[];
} {
  const splitNodes = graph.nodes.reduce((_splitNodes, node) => {
    if (!node.data || node.data.layout.autoLayout) {
      _splitNodes.autoLayoutNodes.push(node);
    } else {
      _splitNodes.manualLayoutNodes.push(node);
    }


    return _splitNodes;
  }, { autoLayoutNodes: [], manualLayoutNodes: [] });


  return splitNodes;
}

/**
 * Adjusts the position of the elements that need to stay in place as the user pans and zooms the canvas.
 */
export function adjustLayoutToFreezeFixedNodes(graph: Graph, canvasRect: CanvasRect) {
  graph.nodes.forEach(node => {
    if (node.data?.layout.fixed) {
      const { x: currentX, y: currentY } = node.position;

      node.position = {
        x: (currentX - canvasRect.x) / canvasRect.zoom,
        y: (currentY - canvasRect.y) / canvasRect.zoom,
      };
    }
  });
}

/**
 * moves the outcome construction nodes up top to create the connection effect
 */
export function adjustOutcomeConstructionNodes(graph: Graph, focusedNodeId: WorkflowState['ui']['focusedNodeId']) {
  const focusedNode = graph.nodes.find(node => node.id === focusedNodeId);

  if (focusedNode?.type === 'outcome') {
    focusedNode.position = {
      ...focusedNode.position,
      y: CANVAS_VERTICAL_PADDING,
    };
  }
}

/**
 * this is the entry function for the lay-outing logic.
 * set the position of the existing nodes in the graph
 */
export function layOutGraph({
  graph,
  uiStatePartial,
  nodeLineage,
  maxIncomingPathLengthMap,
  hasInputAttributeNodeFolder,
  featureFlags,
}: {
  graph: Graph;
  uiStatePartial: {
    activeNodeEditorTab: WorkflowState['ui']['activeNodeEditorTab'];
    canvasWidth: WorkflowState['ui']['canvasProperties']['rect']['width'];
    floatedAnchorNodeId: WorkflowState['ui']['floatedAnchorNodeId'];
    focusedNodeId: WorkflowState['ui']['focusedNodeId'];
  };
  nodeLineage: NodeLineage;
  maxIncomingPathLengthMap: MaxPathLengthMap;
  hasInputAttributeNodeFolder?: boolean;
  featureFlags: Pick<WorkflowState['featureFlagsForStore'], 'sortWorkflowNodesByType'>;
}): void {
  /**
   * sorting the nodes prior to their placement allows us to customize the order in which
   * they are placed in the graph without making changes to the positional algorithm
  */
  sortGraphBeforePlacement(
    graph,
    uiStatePartial,
    nodeLineage,
    featureFlags,
  );

  const {
    autoLayoutNodes,
    manualLayoutNodes,
  } = splitGraphNodesByLayoutMode(graph);

  // memoize the count of nodes per column (array index is column number)
  const columnCountIndex: number[] = new Array(graph.nodes.length).fill(0);

  // Ensure that RiskCalculationNodes are shown at the top of the column by updating columnCountIndex
  // This reserves row 0 for RiskCalculationNodes and ensures that auto-layout nodes in that column start at index 1
  manualLayoutNodes.forEach(node => {
    if (node.type === workflowNodeType.riskCalculation) {
      const columnForNode = getNodeColumn(maxIncomingPathLengthMap, node.id);
      columnCountIndex[columnForNode] = 1; // Reserve row 0, next node goes to row 1
    }
  });

  /**
   * place nodes in the graph by calculating a column and row for them.
   * columns are determined by calculating how many nodes are connected behind a given
   * node, and the row is determined by finding the next available slot in that column
   *
   * to change the order of how nodes are placed, nodes need to be sorted using the desired
   * logic prior to this loop.
   */
  autoLayoutNodes.forEach(node => {
    const columnForNode = getNodeColumn(maxIncomingPathLengthMap, node.id);
    const rowForNode = getNextAvailableRowInColumn(columnForNode, columnCountIndex);
    node.position = getNodePosition({
      row: rowForNode,
      column: columnForNode,
      isChildNode: (
        node.type === workflowNodeType.inputAttribute
        && hasInputAttributeNodeFolder
      ),
    });
    node.zIndex = getNodeZIndex();
    columnCountIndex[columnForNode] += 1;
  });

  /**
   * Manually place nodes that need to be
   */
  let precedenceIndexedNodeCount = 0;
  manualLayoutNodes.forEach(node => {
    /**
     * RiskCalculationNodes are offset upward so that standard nodes align with the "Final Calculation"
     * text (which appears below the top due to padding). This allows both alignment and 8px spacing below.
     */
    if (node.type === workflowNodeType.riskCalculation) {
      const columnForNode = getNodeColumn(maxIncomingPathLengthMap, node.id);
      const basePosition = getNodePosition({
        row: 0,
        column: columnForNode,
        isChildNode: false,
      });
      // Offset upward by 8px so "Final Calculation" text aligns with top of standard nodes
      // (padding + icon space pushes "Final Calculation" down by ~8px from top)
      node.position = {
        ...basePosition,
        y: basePosition.y - 8,
      };
      node.zIndex = getNodeZIndex();
    }

    /**
     * since outcomes and workflow routing nodes are sorted by precedence, we can use their local
     * index as their row. the column pixels (y) are manually computed by taking in the value of
     * the canvas width. this places them outside of the visible canvas, flush to its right
     * edge.
     */
    if (node.type === 'outcome' || node.type === 'workflow') {
      const x = uiStatePartial.canvasWidth;
      // we don't care about the column so we pass a `0`
      const { y } = getNodePosition({ row: precedenceIndexedNodeCount, column: 0 });
      node.position = { x, y };
      node.zIndex = getNodeZIndex();
      precedenceIndexedNodeCount += 1;
    }

    /**
     * importedTailNodes can be placed at the top row and far away into the left edge to
     * accomplish the desired effect without having to manually align them to their corresponding
     * imported node
     */
    if (node.type === 'importedTailNode') {
      const x = 0 - (2 * NODE_WIDTH);
      const { y } = getNodePosition({ row: 0, column: 0 });
      node.position = { x, y };
      node.zIndex = getNodeZIndex();
    }
  });

  graph.edges.forEach(edge => {
    const edgeState = computeEdgeState({
      sourceId: edge.source,
      targetId: edge.target,
      nodeLineage,
      uiStatePartial,
    });
    edge.zIndex = getEdgeZIndex(edgeState);
  });
}
